import { useFeatureIsOn } from "@growthbook/growthbook-react";
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
} from "@mui/material";
import { type JSX } from "react";
import { BTX_DEPENDENT_RATE_BASIS } from "@platform/feature-flags";
import { Typography } from "@platform/design-system";

import { COVERED_BENEFIT_TYPES, RATE_PER_OPTIONS } from "../../constants";
import { RateScheduleFormData } from "../../types/models";
import {
  isBasicLifeCoverageType,
  mapToBackendUnit,
} from "../../utils/rate-helpers";

interface RateUnitSectionProps {
  formData: RateScheduleFormData;
  setFormData: React.Dispatch<React.SetStateAction<RateScheduleFormData>>;
  coverageType?: string;
}

/**
 * Shared volume vs unit controls: radio group for volume/unit selection,
 * and covered type + rate per dropdowns when volume is selected.
 * Used by both age-banded and flat rate configurations. The EE/dependent
 * split question (see DependentRateUnitSection) only applies to Basic Life.
 */
export function RateUnitSection({
  formData,
  setFormData,
  coverageType,
}: RateUnitSectionProps): JSX.Element {
  const dependentRateBasisEnabled = useFeatureIsOn(BTX_DEPENDENT_RATE_BASIS);
  const showDependentSplit =
    dependentRateBasisEnabled &&
    formData.includeDependentRates &&
    isBasicLifeCoverageType(coverageType);

  const handleRatesBasedOnChange = (value: "volume" | "unit"): void => {
    setFormData((prev: RateScheduleFormData) => {
      if (value === "unit") {
        return {
          ...prev,
          ratesBasedOn: value,
          unit: "usd",
          coveredBenefitType: undefined,
          ratePer: undefined,
          // Dependents can never be volume-based when EE is unit-based.
          dependentRatesBasedOn: showDependentSplit
            ? "unit"
            : prev.dependentRatesBasedOn,
        };
      }
      const coveredBenefitType = prev.coveredBenefitType || "coveredBenefit";
      const ratePer = prev.ratePer || "per1000";
      return {
        ...prev,
        ratesBasedOn: value,
        coveredBenefitType,
        ratePer,
        unit: mapToBackendUnit(coveredBenefitType, ratePer),
      };
    });
  };

  const handleCoveredBenefitTypeChange = (value: string): void => {
    setFormData((prev: RateScheduleFormData) => {
      const coveredBenefitType = value as "coveredBenefit" | "coveredPayroll";
      const ratePer = prev.ratePer || "per1000";
      const unit = mapToBackendUnit(coveredBenefitType, ratePer);
      return { ...prev, coveredBenefitType, unit };
    });
  };

  const handleRatePerChange = (value: string): void => {
    setFormData((prev: RateScheduleFormData) => {
      const ratePer = value as "per10" | "per100" | "per1000";
      const coveredBenefitType = prev.coveredBenefitType || "coveredBenefit";
      const unit = mapToBackendUnit(coveredBenefitType, ratePer);
      return { ...prev, ratePer, unit };
    });
  };

  return (
    <>
      <FormControl component="fieldset">
        <FormLabel component="legend">
          {showDependentSplit
            ? "Are EE rates based on volume or unit?"
            : "Are rates based on volume or unit?"}
        </FormLabel>
        <RadioGroup
          row
          value={formData.ratesBasedOn || ""}
          onChange={(e) =>
            handleRatesBasedOnChange(e.target.value as "volume" | "unit")
          }
        >
          <FormControlLabel
            value="volume"
            control={<Radio color="brandPrimary" />}
            label="Volume"
          />
          <FormControlLabel
            value="unit"
            control={<Radio color="brandPrimary" />}
            label="Unit"
          />
        </RadioGroup>
      </FormControl>

      {formData.ratesBasedOn === "volume" && (
        <Box sx={{ border: 1, borderColor: "divider", p: 2, borderRadius: 1 }}>
          <Typography variant="subtitle1" color="textSecondary" sx={{ mb: 2 }}>
            Unit
          </Typography>
          <Stack direction="row" spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Covered Type</InputLabel>
              <Select
                value={formData.coveredBenefitType || "coveredBenefit"}
                label="Covered Type"
                onChange={(e) => handleCoveredBenefitTypeChange(e.target.value)}
              >
                {COVERED_BENEFIT_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Rate Per</InputLabel>
              <Select
                value={formData.ratePer || "per1000"}
                label="Rate Per"
                onChange={(e) => handleRatePerChange(e.target.value)}
              >
                {RATE_PER_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Box>
      )}
    </>
  );
}
